/**
 * F-506-FE-07: SessionHistoryPage
 *
 * Session history list at /sessions.
 * - Lists all sessions for the authenticated doctor
 * - Columns: date/time, patient language, duration, note status badge (color+icon), flag count
 * - Tap to view detail: transcript viewer, note link, export action, delete (with confirmation)
 * - Sortable by date (newest first default)
 * - Paginated
 *
 * Design: Clean data table with good density. Status badges with color+icon.
 * Mobile-first card layout (not a traditional table on small screens).
 * RTL support via CSS logical properties.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SortOrder = 'newest' | 'oldest';

interface SessionListItem {
  id: string;
  status: string;
  patientLanguageCode: string;
  createdAt: string;
  endedAt?: string;
  noteId?: string;
  noteStatus?: string;
  segmentCount?: number;
  flagCount?: number;
}

interface SessionListResponse {
  sessions: SessionListItem[];
  total: number;
  page: number;
  pageSize: number;
}

interface TranscriptSegmentItem {
  id: string;
  speaker: 'DOCTOR' | 'PATIENT';
  originalText: string;
  translatedText: string;
  direction: string;
  createdAt: string;
}

interface SessionDetailFull {
  id: string;
  status: string;
  patientLanguageCode: string;
  createdAt: string;
  endedAt?: string;
  noteId?: string;
  noteStatus?: string;
  segments: TranscriptSegmentItem[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PAGE_SIZE = 20;

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatTime(dateString: string): string {
  return new Date(dateString).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDurationFromMs(startStr: string, endStr?: string): string {
  if (!endStr) return '—';
  const ms = new Date(endStr).getTime() - new Date(startStr).getTime();
  const totalSecs = Math.floor(Math.max(0, ms) / 1_000);
  const h = Math.floor(totalSecs / 3_600);
  const m = Math.floor((totalSecs % 3_600) / 60);
  const s = totalSecs % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function getLanguageName(code: string): string {
  try {
    return new Intl.DisplayNames([navigator.language || 'en'], { type: 'language' }).of(code) ?? code;
  } catch {
    return code;
  }
}

// ---------------------------------------------------------------------------
// Note status badge (color + icon — WCAG)
// ---------------------------------------------------------------------------

interface NoteStatusBadgeProps {
  status?: string;
}

function NoteStatusBadge({ status }: NoteStatusBadgeProps) {
  if (!status) {
    return (
      <span className="status-badge bg-clinical-cream-dark text-clinical-text-muted border border-clinical-border">
        No note
      </span>
    );
  }

  switch (status) {
    case 'GENERATING':
      return (
        <span className="status-badge bg-blue-50 text-blue-700 border border-blue-200 gap-1.5" aria-label="Note status: generating">
          <span className="w-3 h-3 border border-blue-600 border-t-transparent rounded-full animate-spin" aria-hidden="true" />
          Generating
        </span>
      );
    case 'DRAFT':
      return (
        <span className="status-draft gap-1.5" aria-label="Note status: draft">
          <svg className="w-3 h-3 flex-shrink-0 text-clinical-teal" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path d="M11.013 1.427a1.75 1.75 0 0 1 2.474 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474l-8.61 8.61c-.21.21-.47.364-.756.445l-3.251.93a.75.75 0 0 1-.927-.928l.929-3.25c.081-.286.235-.547.445-.758l8.61-8.609Zm.176 4.823L9.75 4.81l-6.286 6.287a.253.253 0 0 0-.064.108l-.558 1.953 1.953-.558a.253.253 0 0 0 .108-.064Zm1.238-3.763a.25.25 0 0 0-.354 0L10.811 3.75l1.439 1.44 1.263-1.263a.25.25 0 0 0 0-.354Z" />
          </svg>
          Draft
        </span>
      );
    case 'COMPLETING':
      return (
        <span className="status-completing gap-1.5" aria-label="Note status: completing">
          <svg className="w-3 h-3 flex-shrink-0 text-amber-600" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path d="M6.457 1.047c.659-1.234 2.427-1.234 3.086 0l6.082 11.378A1.75 1.75 0 0 1 14.082 15H1.918a1.75 1.75 0 0 1-1.543-2.575Zm1.763.707a.25.25 0 0 0-.44 0L1.698 13.132a.25.25 0 0 0 .22.368h12.164a.25.25 0 0 0 .22-.368Zm.53 3.996v2.5a.75.75 0 0 1-1.5 0v-2.5a.75.75 0 0 1 1.5 0ZM9 11a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" />
          </svg>
          Completing
        </span>
      );
    case 'COMPLETED':
    case 'EXPORTED':
      return (
        <span className="status-completed gap-1.5" aria-label="Note status: completed">
          <svg className="w-3 h-3 flex-shrink-0 text-green-600" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z" />
          </svg>
          {status === 'EXPORTED' ? 'Exported' : 'Done'}
        </span>
      );
    case 'GENERATION_FAILED':
      return (
        <span className="status-badge bg-red-50 text-red-700 border border-red-200 gap-1.5" aria-label="Note status: failed">
          <svg className="w-3 h-3 flex-shrink-0 text-safety-red" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13ZM0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8Zm4.22-2.78a.75.75 0 0 1 1.06 0L8 7.94l2.72-2.72a.75.75 0 1 1 1.06 1.06L9.06 9l2.72 2.72a.75.75 0 0 1-1.06 1.06L8 10.06l-2.72 2.72a.75.75 0 0 1-1.06-1.06L6.94 9 4.22 6.28a.75.75 0 0 1 0-1.06Z" />
          </svg>
          Failed
        </span>
      );
    default:
      return (
        <span className="status-badge bg-clinical-cream-dark text-clinical-text-muted border border-clinical-border">
          {status}
        </span>
      );
  }
}

// ---------------------------------------------------------------------------
// Session detail panel
// ---------------------------------------------------------------------------

interface SessionDetailPanelProps {
  sessionId: string;
  onClose: () => void;
  onDeleted: () => void;
}

function SessionDetailPanel({ sessionId, onClose, onDeleted }: SessionDetailPanelProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data: session, isLoading } = useQuery<SessionDetailFull>({
    queryKey: ['session-detail', sessionId],
    queryFn: () => api.get<SessionDetailFull>(`/api/sessions/${sessionId}`),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete<void>(`/api/sessions/${sessionId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sessions'] });
      onDeleted();
    },
  });

  return (
    <div
      className="fixed inset-0 z-40 flex"
      role="dialog"
      aria-modal="true"
      aria-label="Session detail"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-clinical-charcoal/40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel — slides in from inline-end */}
      <div className="relative ms-auto w-full max-w-md bg-white h-full flex flex-col shadow-clinical-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-clinical-border">
          <h2 className="font-heading text-lg font-semibold text-clinical-charcoal">
            Session detail
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-clinical text-clinical-text-muted hover:text-clinical-charcoal hover:bg-clinical-cream-dark transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close detail panel"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="p-5 space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="skeleton h-16 rounded-clinical" />
              ))}
            </div>
          ) : session ? (
            <div>
              {/* Meta */}
              <div className="px-5 py-4 border-b border-clinical-border space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-body text-clinical-text-muted">Date</span>
                  <span className="text-sm font-body font-medium text-clinical-charcoal">
                    {formatDate(session.createdAt)} {formatTime(session.createdAt)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-body text-clinical-text-muted">Duration</span>
                  <span className="text-sm font-body font-medium text-clinical-charcoal">
                    {formatDurationFromMs(session.createdAt, session.endedAt)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-body text-clinical-text-muted">Patient language</span>
                  <span className="text-sm font-body font-medium text-clinical-charcoal">
                    {getLanguageName(session.patientLanguageCode)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-body text-clinical-text-muted">Note status</span>
                  <NoteStatusBadge status={session.noteStatus} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-body text-clinical-text-muted">Exchanges</span>
                  <span className="text-sm font-body font-medium text-clinical-charcoal">
                    {session.segments?.length ?? 0}
                  </span>
                </div>
              </div>

              {/* Transcript viewer */}
              <div className="px-5 py-4">
                <h3 className="font-body text-sm font-semibold text-clinical-charcoal mb-3">
                  Transcript
                </h3>
                {session.segments?.length === 0 ? (
                  <p className="text-sm font-body text-clinical-text-muted">
                    No transcript segments recorded.
                  </p>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {session.segments?.map((seg) => (
                      <div key={seg.id} className="text-sm font-body">
                        <span className="text-xs font-semibold uppercase tracking-wide text-clinical-text-muted">
                          {seg.speaker === 'DOCTOR' ? 'Doctor' : 'Patient'}
                        </span>
                        <p className="text-clinical-charcoal-soft mt-0.5">{seg.translatedText}</p>
                        {seg.originalText !== seg.translatedText && (
                          <p className="text-xs text-clinical-text-muted italic mt-0.5">
                            {seg.originalText}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer actions */}
        {session && (
          <div className="flex-shrink-0 px-5 py-4 border-t border-clinical-border space-y-3">
            {/* Note link */}
            {session.noteId && (
              <button
                onClick={() => navigate(`/notes/${session.noteId}/decision`)}
                className="btn-secondary w-full"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                </svg>
                View clinical note
              </button>
            )}

            {/* Delete with confirmation */}
            {!showDeleteConfirm ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3
                           text-red-600 bg-white border border-red-200 rounded-clinical
                           font-body font-medium text-sm
                           hover:bg-red-50 transition-colors min-h-[44px]"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                </svg>
                Delete session
              </button>
            ) : (
              <div className="space-y-2">
                <p className="text-sm font-body text-clinical-charcoal text-center">
                  Delete this session and its transcript? This cannot be undone.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="btn-secondary flex-1 text-sm py-2.5"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => deleteMutation.mutate()}
                    disabled={deleteMutation.isPending}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5
                               bg-safety-red text-white rounded-clinical font-body font-medium text-sm
                               hover:bg-red-600 transition-colors min-h-[44px]
                               disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Session row card
// ---------------------------------------------------------------------------

interface SessionCardProps {
  session: SessionListItem;
  onSelect: (id: string) => void;
}

function SessionCard({ session, onSelect }: SessionCardProps) {
  return (
    <button
      onClick={() => onSelect(session.id)}
      className="clinical-card-hover w-full text-start p-4"
      aria-label={`Session on ${formatDate(session.createdAt)}, ${getLanguageName(session.patientLanguageCode)}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-body font-semibold text-clinical-charcoal">
              {formatDate(session.createdAt)}
            </span>
            <span className="text-xs font-body text-clinical-text-muted">
              {formatTime(session.createdAt)}
            </span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs font-body text-clinical-text-muted">
              {getLanguageName(session.patientLanguageCode)}
            </span>
            <span className="text-xs font-body text-clinical-text-muted">
              {formatDurationFromMs(session.createdAt, session.endedAt)}
            </span>
            {(session.flagCount ?? 0) > 0 && (
              <span className="inline-flex items-center gap-1 text-xs font-body text-amber-600">
                <svg className="w-3 h-3 text-safety-amber" viewBox="0 0 16 16" fill="currentColor" aria-label="Safety flags">
                  <path d="M6.457 1.047c.659-1.234 2.427-1.234 3.086 0l6.082 11.378A1.75 1.75 0 0 1 14.082 15H1.918a1.75 1.75 0 0 1-1.543-2.575Zm1.763.707a.25.25 0 0 0-.44 0L1.698 13.132a.25.25 0 0 0 .22.368h12.164a.25.25 0 0 0 .22-.368Zm.53 3.996v2.5a.75.75 0 0 1-1.5 0v-2.5a.75.75 0 0 1 1.5 0ZM9 11a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" />
                </svg>
                {session.flagCount} flag{session.flagCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
        <div className="flex-shrink-0">
          <NoteStatusBadge status={session.noteStatus} />
        </div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function SessionHistoryPage() {
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest');
  const [page, setPage] = useState(1);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<SessionListResponse>({
    queryKey: ['sessions', sortOrder, page],
    queryFn: () =>
      api.get<SessionListResponse>(
        `/api/sessions?sort=${sortOrder === 'newest' ? 'desc' : 'asc'}&page=${page}&pageSize=${PAGE_SIZE}`,
      ),
  });

  const sessions = data?.sessions ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function handleDeleted() {
    setSelectedSessionId(null);
    void queryClient.invalidateQueries({ queryKey: ['sessions'] });
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-semibold text-clinical-charcoal">
          Session History
        </h1>

        {/* Sort toggle */}
        <div className="flex items-center gap-1 bg-clinical-cream-dark rounded-clinical p-1">
          <button
            onClick={() => { setSortOrder('newest'); setPage(1); }}
            className={`px-3 py-1.5 rounded-[8px] text-xs font-body font-medium transition-colors min-h-[36px] ${
              sortOrder === 'newest'
                ? 'bg-white text-clinical-charcoal shadow-clinical'
                : 'text-clinical-text-muted hover:text-clinical-charcoal'
            }`}
            aria-pressed={sortOrder === 'newest'}
          >
            Newest
          </button>
          <button
            onClick={() => { setSortOrder('oldest'); setPage(1); }}
            className={`px-3 py-1.5 rounded-[8px] text-xs font-body font-medium transition-colors min-h-[36px] ${
              sortOrder === 'oldest'
                ? 'bg-white text-clinical-charcoal shadow-clinical'
                : 'text-clinical-text-muted hover:text-clinical-charcoal'
            }`}
            aria-pressed={sortOrder === 'oldest'}
          >
            Oldest
          </button>
        </div>
      </div>

      {/* Session list */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="clinical-card p-4">
              <div className="flex items-start gap-3">
                <div className="flex-1 space-y-2">
                  <div className="skeleton h-4 rounded w-2/5" />
                  <div className="skeleton h-3 rounded w-3/5" />
                </div>
                <div className="skeleton h-6 rounded-full w-16" />
              </div>
            </div>
          ))}
        </div>
      ) : sessions.length === 0 ? (
        <div className="clinical-card p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-clinical-cream-dark flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-clinical-text-muted" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 0 1 0 3.75H5.625a1.875 1.875 0 0 1 0-3.75Z" />
            </svg>
          </div>
          <p className="font-body font-medium text-clinical-charcoal text-sm">
            No sessions yet
          </p>
          <p className="text-xs font-body text-clinical-text-muted mt-1">
            Your consultation history will appear here.
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {sessions.map((session) => (
              <SessionCard
                key={session.id}
                session={session}
                onSelect={setSelectedSessionId}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-secondary px-4 py-2 text-sm disabled:opacity-40"
                aria-label="Previous page"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 19.5-7.5-7.5 7.5-7.5" />
                </svg>
                Previous
              </button>
              <span className="text-xs font-body text-clinical-text-muted">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="btn-secondary px-4 py-2 text-sm disabled:opacity-40"
                aria-label="Next page"
              >
                Next
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                </svg>
              </button>
            </div>
          )}

          {/* Total count */}
          <p className="text-xs font-body text-clinical-text-muted text-center mt-4">
            {total} session{total !== 1 ? 's' : ''} total
          </p>
        </>
      )}

      {/* Session detail panel */}
      {selectedSessionId && (
        <SessionDetailPanel
          sessionId={selectedSessionId}
          onClose={() => setSelectedSessionId(null)}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
}
